import { debounce } from '@contentservice/shared/lib/utils';

type ResizeCallback = (width: number, height: number) => void;
type DebouncedCallback = ReturnType<typeof debounce>;

class WindowResizeObserver {
  private static instance: WindowResizeObserver;
  private observer: ResizeObserver | null = null;
  private debouncedCallbacks: Map<ResizeCallback, DebouncedCallback> =
    new Map();
  private callbacks: Map<ResizeCallback, boolean> = new Map(); // callback -> debounce flag
  private currentDimensions: [number, number] = [0, 0];

  private constructor() {
    if (typeof window !== 'undefined') {
      this.currentDimensions = [window.innerWidth, window.innerHeight];
      this.initialize();
    }
  }

  private initialize() {
    if (this.observer) {
      return;
    }

    this.observer = new ResizeObserver(() => {
      this.currentDimensions = [window.innerWidth, window.innerHeight];
      this.notify();
    });

    this.observer.observe(document.documentElement);
  }

  private notify() {
    const [width, height] = this.currentDimensions;
    this.callbacks.forEach((shouldDebounce, callback) => {
      if (shouldDebounce) {
        let debouncedFn = this.debouncedCallbacks.get(callback);
        if (!debouncedFn) {
          debouncedFn = debounce((...args: unknown[]) => {
            const [w, h] = args as [number, number];
            callback(w, h);
          }, 100);
          this.debouncedCallbacks.set(callback, debouncedFn);
        }
        debouncedFn(width, height);
      } else {
        callback(width, height);
      }
    });
  }

  subscribe(callback: ResizeCallback, enableDebounce = true) {
    this.callbacks.set(callback, enableDebounce);
    callback(this.currentDimensions[0], this.currentDimensions[1]);

    return {
      unsubscribe: () => {
        this.callbacks.delete(callback);
        const debouncedFn = this.debouncedCallbacks.get(callback);
        if (debouncedFn) {
          debouncedFn.cancel?.();
          this.debouncedCallbacks.delete(callback);
        }
        if (this.callbacks.size === 0) {
          this.observer?.disconnect();
          this.observer = null;
        }
      },
    };
  }

  static getInstance() {
    return (this.instance ??= new WindowResizeObserver());
  }
}

export const windowResizeObserver = WindowResizeObserver.getInstance();
